import type { Metadata } from "next";

import { DEFAULT_SOCIAL_IMAGE_URL } from "@/consts/seo";
import { BUSINESSES, type EditionBusinessKey } from "@/consts/businesses";

export const SERVICE_HUB_SITE_ORIGIN = "https://www.eddiesedition.com";

export const SERVICE_HUB_SLUGS = [
  "mens-haircut",
  "mens-perm",
  "color",
  "down-perm",
] as const;

export type ServiceHubSlug = (typeof SERVICE_HUB_SLUGS)[number];

export type ServiceHubFaq = {
  question: string;
  answer: string;
};

export type ServiceHub = {
  slug: string;
  path: `/services/${string}`;
  business?: EditionBusinessKey;
  title: string;
  description: string;
  listDescription?: string;
  listPriceLabel?: string;
  heading: string;
  intro: string;
  priceLine: string;
  fromPrice?: number;
  ctaLabel: string;
  ctaNote?: string;
  photosTitle?: string;
  photoCategory?: string;
  tipsTitle: string;
  expect: readonly string[];
  faq: readonly ServiceHubFaq[];
};

export const SERVICE_HUBS: Record<ServiceHubSlug, ServiceHub> = {
  "mens-haircut": {
    slug: "mens-haircut",
    path: "/services/mens-haircut",
    title: "Men's Haircut in Los Angeles | Eddie's Edition",
    description:
      "Precision men's haircuts tailored to your hair, style, and routine at Eddie's Edition in Los Angeles.",
    listDescription:
      "Precision haircut tailored to your hair texture, style, and daily routine.",
    heading: "Men's Haircut",
    intro:
      "A great haircut should work with your hair, not against it. Your stylist will tailor the shape, length, and finish to your texture, growth pattern, and daily routine so the cut looks intentional in the chair and stays easy to manage at home.",
    priceLine: "From $45+ · Price varies by stylist",
    fromPrice: 45,
    ctaLabel: "Book a Men's Haircut",
    photosTitle: "Latest haircut photos",
    photoCategory: "haircuts",
    tipsTitle: "What to expect",
    expect: [
      "Your appointment starts with a consultation about the shape, length, references, and styling routine you have in mind.",
      "For first-time clients, we usually allow more time for consultation so you and your stylist can get to know each other and align on the result.",
      "Some longer hairstyles require more time than a regular haircut. Your stylist will assess the length, shape, and work needed during consultation.",
      "We usually wash your hair before the cut so your stylist can understand how it naturally moves and work with a clean foundation. Your stylist may skip the pre-shampoo when a dry cut is better for your hair or the result you want.",
      "After the cut, we wash away loose hair, then complete the final styling so you can see the finished shape and how to recreate the look at home.",
    ],
    faq: [
      {
        question: "How often should I get a haircut?",
        answer:
          "Most shorter styles stay sharp with a cut every three to five weeks. Longer or more relaxed shapes can often go six to eight weeks.",
      },
      {
        question: "Should I bring reference photos?",
        answer:
          "Yes. Photos help your stylist understand the shape and finish you like. They will also explain how the look can be adapted to your hair texture and growth pattern.",
      },
      {
        question: "Can my stylist help me choose a style?",
        answer:
          "Absolutely. Tell your stylist how you normally wear and maintain your hair, and they can recommend a shape that fits your features, texture, and routine.",
      },
      {
        question: "Does the price vary by stylist?",
        answer:
          "Yes. Haircuts start at $45, and the final price varies by stylist. You can review the exact price when choosing a stylist during booking.",
      },
    ],
  },
  "mens-perm": {
    slug: "mens-perm",
    path: "/services/mens-perm",
    title: "Men's Korean Perm in Los Angeles | Eddie's Edition",
    description:
      "Korean men's perms with haircut included. Natural waves and texture at Eddie's Edition — book online.",
    listDescription:
      "Perm for waves or curls using Korean perm techniques. Our stylists specialize in men’s perms. Haircut is included.",
    heading: "Men's Perm",
    intro:
      'A men\'s Korean perm adds natural wave or soft texture so your hair moves better day to day — without a stiff, "perm" look. At Eddie\'s Edition, the men\'s perm includes a precision haircut so shape and texture work together.',
    priceLine:
      "From $180+ · Haircut included · Price varies by stylist",
    fromPrice: 180,
    ctaLabel: "Book a Men's Perm",
    photosTitle: "Latest perm photos",
    photoCategory: "perms",
    tipsTitle: "Tips after your perm",
    expect: [
      "Avoid shampooing for the first 48–72 hours. This gives the new curl pattern time to set, helping it stay defined and last longer.",
      "Skip hats and anything else that presses your hair flat for the first few days so the curl pattern is not compressed while it sets.",
      "When you do wash, use a gentle, moisturizing shampoo and conditioner to keep curls soft.",
      "Pat your hair dry instead of rubbing it, and style with light curl cream while it is damp.",
    ],
    faq: [
      {
        question: "Is a haircut included with a men's perm?",
        answer:
          "Yes. Every men's perm includes a precision haircut so the shape, length, and curl pattern work together.",
      },
      {
        question: "How long does a men's perm last?",
        answer:
          "Typically around two months, depending on hair growth and how you style it. Soft texture often looks good as it relaxes.",
      },
      {
        question: "How long does my hair need to be for a perm?",
        answer:
          "We can perm hair at any length. The length you need depends on the type of curl and finished look you want, so bring reference photos and your stylist can recommend the best approach for your current length.",
      },
      {
        question: "What is the difference between a Korean perm and an American perm?",
        answer:
          "Compared with many American perm techniques, our Korean perm uses a detached rubber-band rod technique. It lets us create tighter, more defined curls that hold their shape longer while still tailoring the finish to your haircut.",
      },
      {
        question: "Is this a good first perm?",
        answer:
          "Yes for many clients who want natural movement or defined curls. Bring reference photos so your stylist can match the curl and volume you have in mind.",
      },
      {
        question: "Can I combine it with a down perm?",
        answer:
          "Often yes — texture on top with a down perm on the sides/back. Your stylist will confirm what's right for your hair.",
      },
    ],
  },
  color: {
    slug: "color",
    path: "/services/color",
    title: "Hair Color in Los Angeles | Eddie's Edition",
    description:
      "Custom hair color, highlights, and transformations at Eddie's Edition in Los Angeles. Start with a quick service request.",
    listDescription:
      "From highlights to full-color transformations, start with a quick service request.",
    listPriceLabel: "To be determined",
    heading: "Color",
    intro:
      "From subtle dimension and highlights to a complete color change, every color service is planned around your starting shade, hair condition, and goal. Start with a service request by sharing photos of your current hair and desired result.",
    priceLine: "Price to be determined after your service request is reviewed",
    ctaLabel: "Request This Service",
    ctaNote:
      "Submitting a request does not book an appointment. A stylist will send you the confirmed price, appointment length, and booking link.",
    photosTitle: "Latest color photos",
    photoCategory: "color",
    tipsTitle: "Tips after your color",
    expect: [
      "Wait at least 48 hours before shampooing when possible so the fresh color has time to settle.",
      "Use a gentle, color-safe shampoo and conditioner, and rinse with lukewarm rather than very hot water.",
      "Limit frequent heat styling and use heat protection to help preserve shine and tone.",
      "Protect your hair from strong sun and chlorine, which can fade or shift fresh color.",
    ],
    faq: [
      {
        question: "How does a service request work?",
        answer:
          "Upload photos of your current hair and desired result. A stylist will review them and send you the confirmed price, appointment length, and a booking link.",
      },
      {
        question: "How much will my color service cost?",
        answer:
          "Your stylist will determine the price after assessing the technique, product, time, and number of sessions your goal requires.",
      },
      {
        question: "Is a haircut included with color?",
        answer:
          "A haircut is a separate service. Add one when booking if you would like your color and shape refreshed during the same visit.",
      },
      {
        question: "Can you color previously colored hair?",
        answer:
          "Often yes, but previous permanent color, bleach, or at-home color can affect the process. Share your color history in your service request so your stylist can plan appropriately.",
      },
    ],
  },
  "down-perm": {
    slug: "down-perm",
    path: "/services/down-perm",
    title: "Men's Down Perm in Los Angeles | Eddie's Edition",
    description:
      "Down perms to smooth stubborn sides and back hair. Often paired with a men's haircut at Eddie's Edition.",
    heading: "Down Perm",
    intro:
      "A down perm relaxes hair that sticks out on the sides or back so your cut looks cleaner and sits closer to the head. It's popular for thicker or straighter hair that won't stay down with product alone.",
    priceLine:
      "From $60+ · Haircut not included · Often booked with a men's haircut",
    fromPrice: 60,
    ctaLabel: "Book a Down Perm",
    ctaNote:
      "Select Down Perm — and add a Men's Haircut if you want both.",
    tipsTitle: "What to expect",
    expect: [
      "Focused on sides and/or back (not a full curly perm)",
      "Works with your existing cut or a fresh men's haircut",
      "Quick add-on compared to a full men's perm",
    ],
    faq: [
      {
        question: "How is this different from a men's perm?",
        answer:
          "A men's perm adds wave/texture (usually on top). A down perm calms poke-out hair on the sides/back for a cleaner silhouette.",
      },
      {
        question: "Should I book a haircut too?",
        answer:
          "Often yes. Down perm alone doesn't restyle the top; many clients book Men's Haircut + Down Perm together.",
      },
      {
        question: "Who is it for?",
        answer:
          "Anyone whose sides or back won't stay down — especially thicker or straighter hair.",
      },
    ],
  },
};

const HUB_SLUG_BY_TITLE: Record<string, ServiceHubSlug> = {
  "men's haircut": "mens-haircut",
  "mens haircut": "mens-haircut",
  "men's perm": "mens-perm",
  "mens perm": "mens-perm",
  "men's korean perm": "mens-perm",
  "korean men's perm": "mens-perm",
  color: "color",
  "hair color": "color",
  "down perm": "down-perm",
  "men's down perm": "down-perm",
};

/** Same catalog-label cleanup as the Openings service grouping. */
export const normalizeServiceHubTitle = (title: string) =>
  title
    .normalize("NFKC")
    .replace(/[\u2018\u2019\u02bc`\u00b4]/g, "'")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/** Hub path for an identifiable catalog title; undefined otherwise. */
export const getServiceHubPath = (title: string): string | undefined => {
  const slug = HUB_SLUG_BY_TITLE[normalizeServiceHubTitle(title)];
  return slug ? SERVICE_HUBS[slug].path : undefined;
};

/** Hub content for a promoted catalog title; undefined otherwise. */
export const getServiceHub = (title: string): ServiceHub | undefined => {
  const slug = HUB_SLUG_BY_TITLE[normalizeServiceHubTitle(title)];
  return slug ? SERVICE_HUBS[slug] : undefined;
};

export const getLocationBookingPath = (pathId: string, service?: string) =>
  `/appointment/${pathId}${service ? `?service=${encodeURIComponent(service)}#booking` : ""}`;

export const getServiceBookingPath = (
  business: EditionBusinessKey = "eddies",
  service?: string,
) => business === "edition-studio"
  ? getLocationBookingPath("edition-studio", service)
  : `/appointment${service ? `?service=${encodeURIComponent(service)}` : ""}`;

export const getServiceDetailSlug = (title: string): string =>
  HUB_SLUG_BY_TITLE[normalizeServiceHubTitle(title)] ??
  normalizeServiceHubTitle(title)
    .replace(/'/g, "")
    .replace(/&/g, " and ")
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-|-$/g, "");

export const getServiceDetailPath = (
  title: string,
  business: EditionBusinessKey = "eddies",
): `/services/${string}` =>
  `/services/${business === "edition-studio" ? "edition-studio/" : ""}${getServiceDetailSlug(title)}`;

export const buildServiceHubMetadata = (hub: ServiceHub): Metadata => {
  const url = `${SERVICE_HUB_SITE_ORIGIN}${hub.path}`;
  return {
    title: hub.title,
    description: hub.description,
    openGraph: {
      title: hub.title,
      description: hub.description,
      url,
      siteName: BUSINESSES[hub.business ?? "eddies"].branding.displayName,
      type: "website",
      images: [DEFAULT_SOCIAL_IMAGE_URL],
    },
    twitter: {
      card: "summary_large_image",
      title: hub.title,
      description: hub.description,
      images: [DEFAULT_SOCIAL_IMAGE_URL],
    },
    alternates: {
      canonical: url,
    },
  };
};

export const buildServiceHubJsonLd = (hub: ServiceHub) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: hub.heading,
  description: hub.description,
  url: `${SERVICE_HUB_SITE_ORIGIN}${hub.path}`,
  provider: {
    "@type": "Organization",
    name: BUSINESSES[hub.business ?? "eddies"].branding.displayName,
    url: BUSINESSES[hub.business ?? "eddies"].branding.externalSiteUrl ?? SERVICE_HUB_SITE_ORIGIN,
  },
  areaServed: {
    "@type": "City",
    name: "Los Angeles",
  },
  ...(hub.fromPrice !== undefined
    ? {
        offers: {
          "@type": "AggregateOffer",
          url: `${SERVICE_HUB_SITE_ORIGIN}${getServiceBookingPath(hub.business)}`,
          priceCurrency: "USD",
          lowPrice: hub.fromPrice.toFixed(2),
        },
      }
    : {}),
});
